import { useCallback, useEffect, useState } from "react";
import { Plus, Undo2, X, ChevronRight } from "lucide-react";
import api, { inr, fmtDate, fmtErr } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { toast } from "sonner";

const FLOW = ["RETURN_REQUESTED", "PICKUP_SCHEDULED", "IN_TRANSIT", "RECEIVED", "REFUNDED"];
const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none";

export default function Returns() {
  const [returns, setReturns] = useState(null);
  const [delivered, setDelivered] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ shipment_id: "", reason: "", resolution: "refund" });

  const load = useCallback(() => {
    api.get("/returns").then(({ data }) => setReturns(data)).catch(() => {});
    api.get("/shipments", { params: { status: "DELIVERED" } }).then(({ data }) => setDelivered(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/returns", form);
      toast.success("Return requested — reverse pickup scheduled");
      setOpen(false);
      setForm({ shipment_id: "", reason: "", resolution: "refund" });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const advance = async (r) => {
    try {
      const { data } = await api.post(`/returns/${r.return_id}/advance`);
      toast.success(`Return → ${data.status.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="returns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Returns</h1>
          <p className="text-sm text-slate-500">Reverse pickups with automatic refunds to wallet.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="return-create-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">
          <Plus className="h-4 w-4" /> Request Return
        </button>
      </div>

      <div className="mt-6 space-y-4" data-testid="returns-list">
        {(returns || []).map((r) => (
          <div key={r.return_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`return-card-${r.reverse_awb}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <Undo2 className="h-4 w-4 text-shipany-blue" />
                  <p className="mono font-bold text-shipany-navy">{r.reverse_awb}</p>
                  <span className="mono text-xs text-slate-400">← {r.awb}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{r.reason} · {r.courier_name} · {fmtDate(r.created_at)} · refund {inr(r.refund_amount)}</p>
              </div>
              {r.status !== "REFUNDED" && (
                <button onClick={() => advance(r)} data-testid={`return-advance-${r.return_id}`}
                  className="flex items-center gap-1 rounded-lg bg-shipany-navy px-4 py-2 text-xs font-bold text-white hover:bg-shipany-blue">
                  Advance <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="mt-4 flex items-center gap-0">
              {FLOW.map((st, i) => {
                const done = FLOW.indexOf(r.status) >= i;
                return (
                  <div key={st} className="flex flex-1 items-center">
                    <span className={`h-2.5 w-2.5 rounded-full ${done ? "bg-shipany-blue" : "bg-slate-200"}`} />
                    <span className={`ml-1.5 hidden text-[10px] font-semibold sm:block ${done ? "text-shipany-navy" : "text-slate-400"}`}>{st.replace(/_/g, " ")}</span>
                    {i < FLOW.length - 1 && <span className={`mx-1 h-0.5 flex-1 ${FLOW.indexOf(r.status) > i ? "bg-shipany-blue" : "bg-slate-200"}`} />}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {returns && returns.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">No returns yet.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="return-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <form onSubmit={create} className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Request Return</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3.5">
              <select data-testid="return-shipment-select" className={inputCls} required value={form.shipment_id} onChange={(e) => setForm({ ...form, shipment_id: e.target.value })}>
                <option value="">Select delivered shipment…</option>
                {delivered.map((s) => <option key={s.shipment_id} value={s.shipment_id}>{s.awb} · {s.destination_city}</option>)}
              </select>
              <select data-testid="return-reason-select" className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required>
                <option value="">Return reason…</option>
                {["Item damaged", "Received wrong item", "Size not as expected", "No longer needed", "Parcel damaged on arrival", "Other"].map((r) => <option key={r}>{r}</option>)}
              </select>
              <select data-testid="return-resolution-select" className={inputCls} value={form.resolution} onChange={(e) => setForm({ ...form, resolution: e.target.value })}>
                <option value="refund">Refund to wallet</option>
                <option value="replacement">Replacement</option>
              </select>
            </div>
            <button data-testid="return-submit-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Create Return</button>
          </form>
        </div>
      )}
    </div>
  );
}
